import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuCode } from 'react-icons/lu';
import { getApiErrorMessage } from '../../utils/apiError';
import '../../styles/modal.css';

// Fallback for the 202 message of /crawler/generate/crwal/project/script, which
// generates the scripts in the background and notifies the user when done.
export const SCRIPT_GENERATION_STARTED_MESSAGE =
  'Project script generation started successfully. You will receive a notification once execution is completed.';

// test_case_type sent to /crawler/generate/crwal/project/script.
// Mirrors backend TestCaseType: MANUAL = 1, AI_GENERATED = 3; null compiles both.
const COMPILE_SCRIPT_OPTIONS = [
  {
    key: 'manual',
    testCaseType: 1,
    title: 'Manual Test Cases',
    description: 'Generate scripts only for the test cases written manually',
    iconStroke: '#2563eb',
    iconPath:
      'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  },
  {
    key: 'ai',
    testCaseType: 3,
    title: 'AI Generated Test Cases',
    description: 'Generate scripts only for the test cases created by AI',
    iconStroke: '#7c3aed',
    iconPath:
      'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
  },
  {
    key: 'both',
    testCaseType: null,
    title: 'All Test Cases (Manual + AI)',
    description: 'Generate scripts for both manual and AI generated test cases',
    iconStroke: '#16a34a',
    iconPath:
      'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
  },
];

/**
 * Lets the user pick which test cases "Compile and Generate" builds scripts for.
 * onGenerate receives the test_case_type to send (null for both) and should
 * reject with the API error so its message is shown inside the modal.
 */
const CompileScriptsOptionsModal = ({ isOpen, onClose, onGenerate }) => {
  const [selectedKey, setSelectedKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Start every opening with no option picked and no stale error
  useEffect(() => {
    if (isOpen) {
      setSelectedKey(null);
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleGenerate = async () => {
    const option = COMPILE_SCRIPT_OPTIONS.find((o) => o.key === selectedKey);
    if (!option || submitting) return;

    setSubmitting(true);
    setError('');
    try {
      await onGenerate(option.testCaseType);
    } catch (err) {
      console.error('Error starting project script generation:', err);
      setError(getApiErrorMessage(err, 'Failed to start script generation. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      data-rt-modal-overlay
      className="create-test-case-options-modal-overlay compile-scripts-modal-overlay"
      onClick={handleClose}
    >
      <div
        className="create-test-case-options-modal-panel compile-scripts-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="compile-scripts-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="create-test-case-options-modal-header compile-scripts-modal-header">
          <h2 id="compile-scripts-modal-title">Compile and Generate Scripts</h2>
          <p>Choose which test cases you want to generate scripts for</p>
        </div>

        <div className="compile-scripts-modal-body">
          <fieldset className="compile-scripts-options" disabled={submitting}>
            <legend className="compile-scripts-sr-only">Test cases to compile</legend>
            {COMPILE_SCRIPT_OPTIONS.map((option) => {
              const isSelected = selectedKey === option.key;
              return (
                <label
                  key={option.key}
                  className={`compile-scripts-option${isSelected ? ' is-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="compile-scripts-option"
                    value={option.key}
                    checked={isSelected}
                    onChange={() => {
                      setSelectedKey(option.key);
                      setError('');
                    }}
                    className="compile-scripts-option-radio"
                  />
                  <span className={`compile-scripts-option-icon compile-${option.key}`} aria-hidden="true">
                    <svg width="20" height="20" fill="none" stroke={option.iconStroke} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={option.iconPath} />
                    </svg>
                  </span>
                  <span className="compile-scripts-option-text">
                    <span className="compile-scripts-option-title">{option.title}</span>
                    <span className="compile-scripts-option-desc">{option.description}</span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          {error && (
            <div className="compile-scripts-error" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="compile-scripts-modal-footer">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="create-test-case-options-cancel-btn compile-scripts-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={!selectedKey || submitting}
            className="compile-scripts-generate-btn"
            title={selectedKey ? undefined : 'Select an option to continue'}
          >
            {submitting ? (
              <span className="compile-scripts-spinner" aria-hidden="true" />
            ) : (
              <LuCode size={16} aria-hidden="true" />
            )}
            {submitting ? 'Generating…' : 'Compile and Generate'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CompileScriptsOptionsModal;
